// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { useBunzlDispatch } from "@store/Framework/Store/Bunzl/BunzlReducer";
import List from "@store/Library/Components/Icons/List";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import React from "react";
import { css } from "styled-components";
import ListTreeViewIcon from "./LinkTreeViewIcon";

const getListIconClickableStyles = (isToggled: boolean, baseStyles: InjectableCss) => css`
    ${baseStyles.css}
    color: ${isToggled ? "gray" : getColor("primary")};
    outline: ${isToggled ? `2px solid gray` : css`2px solid ${getColor("primary")}`};
    &:focus {
        outline: ${isToggled ? `2px solid gray` : css`2px solid ${getColor("primary")}`};
    }
`;

const getCategoryIconClickableStyles = (isToggled: boolean, baseStyles: InjectableCss) => css`
    ${baseStyles.css}
    padding-top: ${pxToRem(5)};
    color: ${isToggled ? getColor("primary") : "gray"};
    outline: ${isToggled ? css`2px solid ${getColor("primary")}` : `2px solid gray`};
    &:focus {
        outline: ${isToggled ? css`2px solid ${getColor("primary")}` : `2px solid gray`};
    }
`;

const getCategoryIconStyles = (isToggled: boolean, baseStyles: InjectableCss) => css`
    ${baseStyles.css}
    color: ${isToggled ? getColor("primary") : "gray"};
`;

const getListIconStyles = (isToggled: boolean, baseStyles: InjectableCss) => css`
    ${baseStyles.css}
    color: ${isToggled ? "gray" : getColor("primary")};
`;
interface MyListDetailsCategoryToggleStyles {
    wrapper?: InjectableCss;
    labelTypographyBaseStyles?: TypographyPresentationProps;
    iconRowWrapper?: InjectableCss;
    iconClickableBaseStyles?: InjectableCss;
    iconBaseStyles?: InjectableCss;
}

const myListDetailsCategoryToggleStyles: MyListDetailsCategoryToggleStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            min-width: ${pxToRem(100)};
            align-items: center;
            gap: ${pxToRem(12)};
        `,
    },
    iconClickableBaseStyles: {
        css: css`
            border-radius: ${pxToRem(6)};
            justify-content: center;
            border: none;
            align-items: center;
            height: ${pxToRem(35)};
            width: ${pxToRem(35)};
            transition: all 0.3s ease-in-out;
            &:hover {
                color: ${getColor("secondary")};
                svg {
                    color: ${getColor("secondary")};
                }
                outline: 2px solid ${getColor("secondary")};
            }
        `,
    },
    labelTypographyBaseStyles: {
        css: css`
            min-width: ${pxToRem(40)};
            opacity: 0;
            font-weight: 700;
            transition: all 0.3s ease-in-out;
            color: ${getColor("primary")};
        `,
    },
    iconRowWrapper: {
        css: css`
            padding-inline: ${pxToRem(16)};
            display: flex;
            gap: ${pxToRem(18)};
        `,
    },
    iconBaseStyles: {
        css: css`
            &:hover {
                color: ${getColor("secondary")};
            }
        `,
    },
};

const getLabelTypographyStyles = (isHovering: boolean, baseStyles: TypographyPresentationProps) => css`
    ${baseStyles.css}
    opacity: ${isHovering ? 1 : 0};
`;
interface MyListDetailsCategoryToggleProps {
    extendedStyles?: MyListDetailsCategoryToggleStyles;
    overrideStyles?: MyListDetailsCategoryToggleStyles;
}

export const MyListDetailsCategoryToggle: React.FC<MyListDetailsCategoryToggleProps> = ({
    extendedStyles,
    overrideStyles,
}) => {
    const [labelText, setLabelText] = React.useState("List View");
    const [isHovering, setIsHovering] = React.useState(false);
    const bunzlDispatch = useBunzlDispatch();
    const dispatch = useAppDispatch();
    const styles = useExpandProps(myListDetailsCategoryToggleStyles, extendedStyles, overrideStyles);
    const wishlistLinesParameter = useAppSelector(state => state.pages.myListDetails.loadWishListLinesParameter);
    const isToggled = useAppSelector(state => state.bunzl.pages.myListDetailsCategory.showCategoryView);
    const handleToggle = (isToggled: boolean) => {
        // we need to set the sort to a valid sort for the category view
        const sortWithCategory = wishlistLinesParameter.sort?.includes("Category")
            ? wishlistLinesParameter.sort
            : "Category.ShortDescription";

        const sort = isToggled ? sortWithCategory : "SortOrder";
        bunzlDispatch({ type: "Pages/MyListDetails/SetShowCategoryView", showCategoryView: isToggled });

        const additionalQueryStringParameters = {
            ...wishlistLinesParameter.additionalQueryStringParameters,
            IncludeCategory: isToggled,
        };
        dispatch(
            updateLoadWishListLinesParameter({
                additionalQueryStringParameters,
                sort,
            }),
        );
        dispatch(loadWishListLines());
    };
    const handleMouseOver = (labelText: string) => {
        setLabelText(labelText);
        setIsHovering(true);
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography mergeCss css={getLabelTypographyStyles(isHovering, { ...styles.labelTypographyBaseStyles })}>
                {labelText}
            </Typography>
            <StyledWrapper {...styles.iconRowWrapper}>
                <Clickable
                    onClick={() => handleToggle(false)}
                    onMouseEnter={() => handleMouseOver(translate("List View"))}
                    onMouseLeave={() => setIsHovering(false)}
                    css={getListIconClickableStyles(isToggled, { ...styles.iconClickableBaseStyles })}
                >
                    <Icon css={getListIconStyles(isToggled, { ...styles.iconBaseStyles })} size={30} src={List} />
                </Clickable>
                <Clickable
                    onClick={() => handleToggle(true)}
                    onMouseEnter={() => handleMouseOver(translate("Category View"))}
                    onMouseLeave={() => setIsHovering(false)}
                    css={getCategoryIconClickableStyles(isToggled, { ...styles.iconClickableBaseStyles })}
                >
                    <Icon
                        css={getCategoryIconStyles(isToggled, { ...styles.iconBaseStyles })}
                        size={30}
                        src={ListTreeViewIcon}
                    />
                </Clickable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: MyListDetailsCategoryToggle,
    definition: {
        group: "My Lists Details",
        displayName: "Category Toggle",
    },
};
export default widgetModule;
